import React, { useEffect, useRef, useState, useCallback } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Chip,
} from '@mui/material'
import StopIcon from '@mui/icons-material/Stop'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import Tooltip from '@mui/material/Tooltip'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import { useAuth } from '../../App'
import TtsStreamClient from '../../services/ttsStreamClient'
import StreamingIndicator from './StreamingIndicator'
import { tokens } from '../../theme/tokens'

interface TtsStreamingModeProps {
  voiceId: string
  text: string
  setText: (text: string) => void
  language: string
  /** The server-advertised `streaming` capability for the selected language. It
   *  is true for every supported language, so this only ever disables Connect if
   *  the service starts saying otherwise — it is NOT an en/fr tier check. */
  streamingSupported?: boolean
  /** True when the selected language is served at the lowest latency. Purely
   *  informational: the other languages stream too, just not as fast. */
  lowLatency?: boolean
}

type ConnectionState = 'idle' | 'connecting' | 'streaming' | 'error' | 'disconnected'

// Audio level visualizer bars (M16)
const AudioLevelVisualizer: React.FC<{ isPlaying: boolean }> = ({ isPlaying }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: '3px',
        height: 20,
        '@media (prefers-reduced-motion: reduce)': {
          '& .audio-bar': {
            animation: 'none !important',
            height: '4px !important',
          },
        },
      }}
    >
      {[0, 1, 2, 3, 4].map((i) => (
        <Box
          key={i}
          className="audio-bar"
          sx={{
            width: 4,
            borderRadius: 1,
            backgroundColor: tokens.colors.tts.light,
            height: isPlaying ? undefined : '4px',
            ...(isPlaying && {
              animation: `audioBar${i} ${0.6 + i * 0.12}s ease-in-out infinite alternate`,
            }),
            '@keyframes audioBar0': {
              from: { height: '4px' },
              to: { height: '16px' },
            },
            '@keyframes audioBar1': {
              from: { height: '6px' },
              to: { height: '20px' },
            },
            '@keyframes audioBar2': {
              from: { height: '8px' },
              to: { height: '14px' },
            },
            '@keyframes audioBar3': {
              from: { height: '4px' },
              to: { height: '18px' },
            },
            '@keyframes audioBar4': {
              from: { height: '6px' },
              to: { height: '12px' },
            },
          }}
        />
      ))}
    </Box>
  )
}

const TtsStreamingMode: React.FC<TtsStreamingModeProps> = ({
  voiceId,
  text,
  setText,
  language,
  streamingSupported = true,
  lowLatency = false,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  // tts.light (#00758a) is ~4.3:1 on light bg — use tts.main (#004a57) in light mode instead
  const ttsTextColor = isDark ? tokens.colors.tts.light : tokens.colors.tts.main
  const auth = useAuth()

  const clientRef = useRef<TtsStreamClient | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const workletRef = useRef<AudioWorkletNode | null>(null)
  const audioCtxCreatedRef = useRef<boolean>(false)
  const sentTextRef = useRef<string>('')
  const recordedChunksRef = useRef<Float32Array[]>([])
  const [canDownload, setCanDownload] = useState(false)

  const [connectionState, setConnectionState] = useState<ConnectionState>('idle')
  const [error, setError] = useState<string | null>(null)
  const [chunkCount, setChunkCount] = useState(0)
  const [connectedAt, setConnectedAt] = useState<number | null>(null)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Elapsed timer
  useEffect(() => {
    if (connectionState !== 'streaming' && connectionState !== 'connecting') return
    if (!connectedAt) return
    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - connectedAt) / 1000))
    }, 1000)
    return () => clearInterval(interval)
  }, [connectionState, connectedAt])

  const initAudioWorklet = useCallback(async () => {
    if (!audioCtxCreatedRef.current) {
      const ctx = new AudioContext({ sampleRate: 24000 })
      try {
        await ctx.audioWorklet.addModule('/audio-output-processor.js')
      } catch (err) {
        throw new Error('Failed to load audio processor. Please refresh the page.')
      }
      const worklet = new AudioWorkletNode(ctx, 'audio-output-processor')
      worklet.connect(ctx.destination)
      audioCtxRef.current = ctx
      workletRef.current = worklet
      audioCtxCreatedRef.current = true
    }
    if (audioCtxRef.current?.state === 'suspended') {
      await audioCtxRef.current.resume()
    }
  }, [])

  const handleDownloadRecording = useCallback(() => {
    const chunks = recordedChunksRef.current
    if (chunks.length === 0) return
    // Concatenate all chunks
    const totalLen = chunks.reduce((acc, c) => acc + c.length, 0)
    const merged = new Float32Array(totalLen)
    let offset = 0
    for (const c of chunks) {
      merged.set(c, offset)
      offset += c.length
    }
    // Create WAV file
    const sampleRate = 24000
    const numChannels = 1
    const bitsPerSample = 16
    const byteRate = sampleRate * numChannels * (bitsPerSample / 8)
    const blockAlign = numChannels * (bitsPerSample / 8)
    const dataSize = merged.length * (bitsPerSample / 8)
    const buffer = new ArrayBuffer(44 + dataSize)
    const view = new DataView(buffer)
    const writeStr = (off: number, str: string) => { for (let i = 0; i < str.length; i++) view.setUint8(off + i, str.charCodeAt(i)) }
    writeStr(0, 'RIFF'); view.setUint32(4, 36 + dataSize, true); writeStr(8, 'WAVE')
    writeStr(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true)
    view.setUint16(22, numChannels, true); view.setUint32(24, sampleRate, true)
    view.setUint32(28, byteRate, true); view.setUint16(32, blockAlign, true)
    view.setUint16(34, bitsPerSample, true); writeStr(36, 'data'); view.setUint32(40, dataSize, true)
    for (let i = 0; i < merged.length; i++) {
      const s = Math.max(-1, Math.min(1, merged[i]))
      view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true)
    }
    const blob = new Blob([buffer], { type: 'audio/wav' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = 'tts-stream-recording.wav'; a.click()
    URL.revokeObjectURL(url)
  }, [])

  const scheduleAudioChunk = useCallback((chunk: ArrayBuffer) => {
    const samples = new Float32Array(chunk)
    if (samples.length === 0) return

    // Record ALL chunks (including silent) for WAV download
    recordedChunksRef.current.push(new Float32Array(samples))
    setCanDownload(true)

    // Skip near-silent warmup chunks — silence-skip stays in React component
    let maxAbs = 0
    for (let i = 0; i < samples.length; i++) {
      const abs = Math.abs(samples[i])
      if (abs > maxAbs) maxAbs = abs
    }
    if (maxAbs < 0.02) return

    // Feed non-silent chunks to worklet
    workletRef.current?.port.postMessage({ type: 'audio', samples })
  }, [])

  const handleStop = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    if (clientRef.current) {
      clientRef.current.disconnect()
      clientRef.current = null
    }
    // Flush worklet before closing
    if (workletRef.current) {
      workletRef.current.port.postMessage({ type: 'flush' })
      workletRef.current.port.close()
      workletRef.current.disconnect()
      workletRef.current = null
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close()
      audioCtxRef.current = null
      audioCtxCreatedRef.current = false
    }
    setConnectionState('disconnected')
    setConnectedAt(null)
    setChunkCount(0)
  }, [])

  // Explicit connect (H12) — no auto-connect on voiceId change
  const handleConnect = useCallback(async () => {
    if (!voiceId) return

    setConnectionState('connecting')
    setError(null)
    setChunkCount(0)
    recordedChunksRef.current = []
    setCanDownload(false)

    // Initialize AudioWorklet on user gesture (connect button click)
    try {
      await initAudioWorklet()
    } catch (err: any) {
      setError(err?.message || 'Failed to initialize audio')
      setConnectionState('error')
      return
    }

    const client = new TtsStreamClient()
    clientRef.current = client

    client.onAudioChunk((chunk: ArrayBuffer) => {
      setConnectionState('streaming')
      setChunkCount((c) => c + 1)
      scheduleAudioChunk(chunk)
    })

    client.onTextMessage((msg: any) => {
      if (msg.type === 'done') {
        // Stream completed naturally
      }
    })

    client.onError((err: string) => {
      if (err === 'auth_failed') {
        setError(t('tts.errorAuth', 'Authentication failed. Please sign in again.'))
      } else if (err === 'insufficient_credits') {
        setError(
          t(
            'tts.error402',
            'Insufficient balance. Please top up your wallet via the billing menu to use TTS.'
          )
        )
      } else {
        setError(err)
      }
      setConnectionState('error')
    })

    try {
      const firebaseToken = await auth.getToken()
      const wsToken = await client.getWsToken(firebaseToken)
      // voiceId is the catalog id, used verbatim; the language rides alongside
      // it. Vocal range needs no parameter — it's fixed by the chosen voice.
      await client.connect(voiceId, wsToken, language)
      setConnectionState('connecting')
      setConnectedAt(Date.now())
      // Send existing text immediately after connecting
      sentTextRef.current = ''
      if (text.trim()) {
        setTimeout(() => {
          if (client.isConnected()) {
            const normalized = text.replace(/[\n\r\t\v\f]+/g, ' ')
            client.sendText(normalized)
            sentTextRef.current = normalized
          }
        }, 200)
      }
    } catch (err: any) {
      setError(err?.message || t('tts.errorConnection', 'Failed to connect to streaming service'))
      setConnectionState('error')
    }
  }, [voiceId, language, auth, t, initAudioWorklet, scheduleAudioChunk])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (clientRef.current) {
        clientRef.current.disconnect()
        clientRef.current = null
      }
      if (workletRef.current) {
        workletRef.current.port.postMessage({ type: 'flush' })
        workletRef.current.port.close()
        workletRef.current.disconnect()
        workletRef.current = null
      }
      if (audioCtxRef.current) {
        audioCtxRef.current.close()
        audioCtxRef.current = null
        audioCtxCreatedRef.current = false
      }
    }
  }, [])

  // Debounced send: sends only the NEW text (delta) after 500ms of no typing.
  // This matches the original working approach but avoids re-sending old text.
  useEffect(() => {
    if (!clientRef.current?.isConnected()) return

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      if (!clientRef.current?.isConnected()) return
      const normalized = text.replace(/[\n\r\t\v\f]+/g, ' ')
      if (normalized.length <= sentTextRef.current.length) return
      const delta = normalized.slice(sentTextRef.current.length)
      if (delta.trim()) {
        clientRef.current.sendText(delta)
        sentTextRef.current = normalized
      }
    }, 500)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [text])

  const indicatorState: 'idle' | 'connecting' | 'streaming' | 'error' =
    connectionState === 'disconnected' ? 'idle' : connectionState === 'error' ? 'error' : connectionState as any

  const isConnected = connectionState === 'streaming' || connectionState === 'connecting'
  const isPlaying = connectionState === 'streaming'

  // Connection status chip (H12)
  const statusChip = isConnected ? (
    connectionState === 'connecting' ? (
      <Chip
        label={t('tts.connecting', 'Connecting...')}
        size="small"
        color="warning"
        variant="outlined"
        sx={{ fontWeight: 600, fontSize: '0.75rem' }}
      />
    ) : (
      <Chip
        label={t('tts.connected', 'Connected')}
        size="small"
        color="success"
        variant="outlined"
        sx={{ fontWeight: 600, fontSize: '0.75rem' }}
      />
    )
  ) : (
    <Chip
      label={t('tts.disconnected', 'Disconnected')}
      size="small"
      color="default"
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: '0.75rem' }}
    />
  )

  return (
    <Box>
      {/* Status bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1.5,
          px: 0.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }} role="status" aria-live="polite" aria-atomic="true">
          <StreamingIndicator state={indicatorState} />
          {statusChip}
          {isPlaying && <AudioLevelVisualizer isPlaying={isPlaying} />}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Latency, stated where it's actionable — right next to Connect. Every
              language streams, so we say nothing at all for the rest rather than
              implying a missing capability. */}
          {!isConnected && streamingSupported && lowLatency && (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', maxWidth: 260, textAlign: 'right', lineHeight: 1.3 }}
            >
              {t('tts.lowestLatency', 'Lowest-latency streaming available for this language')}
            </Typography>
          )}
          {!isConnected && (
            <Tooltip
              title={
                !streamingSupported
                  ? t('tts.streamingUnavailable', 'Streaming is not available for this language')
                  : ''
              }
              arrow
            >
              <span>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={handleConnect}
                  disabled={!voiceId || !streamingSupported}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    background: tokens.gradients.tts,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${tokens.colors.tts.light} 0%, ${tokens.colors.tts.main} 100%)`,
                    },
                  }}
                >
                  {t('tts.connect', 'Connect')}
                </Button>
              </span>
            </Tooltip>
          )}
          {isConnected && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<StopIcon />}
              onClick={handleStop}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              {t('tts.disconnect', 'Disconnect')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Error */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Text input */}
      <Box
        sx={{
          p: 2.5,
          mb: 2,
          borderRadius: 3,
          backgroundColor: 'rgba(0,74,87,0.06)',
          border: `1px solid ${tokens.colors.tts.main}26`,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
          {t('tts.streamingTextLabel', 'Live text (sent on change)')}
        </Typography>

        <TextField
          multiline
          minRows={4}
          maxRows={10}
          fullWidth
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('tts.textPlaceholder', 'Enter text here...')}
          inputProps={{ 'aria-label': t('tts.streamingTextLabel', 'Live text (sent on change)') }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: tokens.colors.tts.main,
              },
            },
          }}
        />
      </Box>

      {/* Live metrics */}
      {(connectionState === 'streaming' || chunkCount > 0) && (
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            px: 1,
            py: 1,
            borderRadius: 2,
            backgroundColor: `${tokens.colors.tts.main}0d`,
            border: `1px solid ${tokens.colors.tts.main}1a`,
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('tts.chunksReceived', 'Chunks')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: ttsTextColor }}>
              {chunkCount}
            </Typography>
          </Box>
          {connectedAt && (
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('tts.duration', 'Duration')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: ttsTextColor }}>
                {elapsedSeconds}s
              </Typography>
            </Box>
          )}
          {canDownload && (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
              <Button
                size="small"
                variant="outlined"
                onClick={handleDownloadRecording}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}
              >
                {t('tts.downloadRecording', 'Download WAV')}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

export default TtsStreamingMode
